import { supabase } from '@/integrations/supabase/client';

/**
 * Bringing a DELETED ticket back to the general lot.
 *
 * The legacy client helper wrote `status='Paused'`, `timer_status='paused'`,
 * `paused_at` and `paused_by='user'` — manufacturing a technician pause for a
 * person who never paused — and assumed a Deleted row had already had its
 * timer sessions closed, which stopped being true when 8G.2 removed the forced
 * close from deletion.
 *
 * The server authority refuses contradictory Timer V2 evidence, refuses
 * malformed tombstoned rows, and returns the ticket to the lot as ACTIVE and
 * UNASSIGNED — Active because `Blocked` appears in none of the parking-lot or
 * scheduling filters and would leave the restored vehicle invisible.
 */
export type RestoreOutcome =
  /** Back in the general lot, live and unassigned. */
  | 'RESTORED'
  /** Ended while technician work was still open. A person must resolve it. */
  | 'TERMINAL_EVIDENCE_CONFLICT'
  /** A tombstone on a non-terminal row. Not silently normalised. */
  | 'TERMINAL_STATE_CONFLICT'
  /** Cancelled: lineage was released on cancellation, so this is not a restore. */
  | 'UNSUPPORTED_TERMINAL_STATE'
  /** Already live. */
  | 'NOT_TERMINAL'
  /** Unknown, another tenant's, or one this caller may not restore — one answer. */
  | 'TICKET_NOT_AVAILABLE'
  /** No server answer. Does NOT mean nothing committed. */
  | 'SUBMIT_FAILED'
  /**
   * 8G.2C.4 §16: the server SAID `RESTORED`, but the response it came with is
   * not internally consistent — a missing canonical shop, a status that is not
   * `Active`, or a source state that is not `Deleted`.
   *
   * This is deliberately NOT treated as a success and NOT treated as a
   * refusal. Something may well have committed, so the client must not
   * re-fire the human Restore act; but the attribution it would need to emit a
   * downstream record is absent, so it must not invent one either. The only
   * honest response is to re-read authoritative state.
   */
  | 'RESTORED_UNCONFIRMED';

export interface RestoreResult {
  outcome: RestoreOutcome;
  ticketId: string;
  /** Final status on success. */
  status: string | null;
  /** Canonical shop, resolved and authorized server-side under the lock. */
  shopId: string | null;
  /** Historical context only — restore never re-assigns. */
  releasedAssignedTechId: string | null;
  restoredFrom: string | null;
  activeSessions: number;
  unresolvedPauses: number;
  awaitingReview: number;
  note: string | null;
}

const KNOWN: RestoreOutcome[] = [
  'RESTORED', 'TERMINAL_EVIDENCE_CONFLICT', 'TERMINAL_STATE_CONFLICT',
  'UNSUPPORTED_TERMINAL_STATE', 'NOT_TERMINAL', 'TICKET_NOT_AVAILABLE',
];

export async function restoreDeletedTicket(
  ticketId: string, reason?: string | null,
): Promise<RestoreResult> {
  const { data, error } = await (supabase as any).rpc('restore_ticket_if_quiescent', {
    p_ticket_id: ticketId,
    p_reason: reason ?? null,
  });
  const empty = {
    ticketId, status: null, shopId: null, releasedAssignedTechId: null,
    restoredFrom: null, activeSessions: 0, unresolvedPauses: 0, awaitingReview: 0,
  };
  if (error) {
    console.warn('[restoreDeletedTicket] failed', error);
    return { ...empty, outcome: 'SUBMIT_FAILED', note: error.message };
  }
  const r = (data ?? {}) as Record<string, unknown>;
  // Unknown outcomes FAIL CLOSED. The vocabulary belongs to the server.
  let outcome = KNOWN.includes(r.outcome as RestoreOutcome)
    ? (r.outcome as RestoreOutcome)
    : 'SUBMIT_FAILED';

  // 8G.2C.4 §16 — A SUCCESS MUST BE INTERNALLY CONSISTENT.
  //
  // `didRestore` used to key on the outcome string alone, so a malformed
  // success — `{ outcome: 'RESTORED' }` with no shop and no status — was
  // treated as a completed restore. The surfaces then emitted Pensieve and
  // OCIS records built from `restored.shopId` and `restored.status`, which
  // means a downstream record attributed to `null` shop, or a status field
  // filled in from the client's own guess about what the server did.
  //
  // The canonical success is specific: Active, in the general lot, unassigned,
  // restored FROM Deleted, in a shop the server resolved and authorized. If
  // any of that is missing the client does not know what happened, and the
  // safe reading is NOT "it failed" — the transaction may well have committed.
  // It is "re-read the ticket", which is what RESTORED_UNCONFIRMED means.
  const shopId = (r.shop_id as string) ?? null;
  const status = (r.status as string) ?? null;
  const restoredFrom = (r.restored_from as string) ?? null;
  if (outcome === 'RESTORED'
      && !(shopId && status === 'Active' && restoredFrom === 'Deleted')) {
    console.warn('[restoreDeletedTicket] RESTORED without canonical attribution', {
      ticketId, shopId, status, restoredFrom,
    });
    outcome = 'RESTORED_UNCONFIRMED';
  }

  return {
    outcome,
    ticketId: (r.ticket_id as string) ?? ticketId,
    status,
    shopId,
    releasedAssignedTechId: (r.released_assigned_tech_id as string) ?? null,
    restoredFrom,
    activeSessions: Number(r.active_sessions ?? 0),
    unresolvedPauses: Number(r.unresolved_pauses ?? 0),
    awaitingReview: Number(r.awaiting_review ?? 0),
    note: (r.note as string) ?? null,
  };
}

/**
 * A CONFIRMED restore. `RESTORED_UNCONFIRMED` is excluded on purpose: callers
 * gate their durable downstream writes on this, and an unconfirmed restore has
 * no canonical shop to attribute them to.
 */
export const didRestore = (o: RestoreOutcome) => o === 'RESTORED';

/**
 * The server may have committed. Reload rather than retry.
 *
 * 8G.2C.4 §17 — THE MINIMUM LOST-RESPONSE LAW FOR THIS PHASE. The
 * `TICKET_RESTORED` row written inside `restore_ticket_if_quiescent` is the
 * authoritative record of the human Restore act: exactly one per successful
 * transaction, none for a refusal. So when the client cannot confirm what
 * happened — transport failure, or a success it cannot validate — re-firing
 * Restore is not a safe retry, because the first attempt may already have
 * produced that row. The client re-reads state instead and lets the operator
 * decide.
 *
 * Pensieve and OCIS records remain DERIVED downstream records. Their
 * consolidation with the server audit act is explicitly OPEN, pending 8G.3.
 */
export const needsRestoreReload = (o: RestoreOutcome) =>
  o === 'RESTORED_UNCONFIRMED' || o === 'SUBMIT_FAILED';

export function describeRestoreRefusal(r: RestoreResult): string {
  switch (r.outcome) {
    case 'TERMINAL_EVIDENCE_CONFLICT':
      if (r.activeSessions > 0) {
        return `This ticket was deleted while ${r.activeSessions} technician(s) still had work running. Someone needs to resolve that first.`;
      }
      if (r.unresolvedPauses > 0) {
        return 'This ticket was deleted while a technician was still paused on it. Someone needs to resolve that first.';
      }
      return 'This ticket still has technician time that needs allocating before it can come back.';
    case 'TERMINAL_STATE_CONFLICT':
      return 'This ticket has a deletion mark but is not in a deleted state. Someone needs to reconcile it.';
    case 'UNSUPPORTED_TERMINAL_STATE':
      return 'Cancelled visits cannot be restored here — cancelling released the ticket from its chain.';
    case 'NOT_TERMINAL':
      return 'This ticket is already active.';
    case 'TICKET_NOT_AVAILABLE':
      return 'This ticket is not available.';
    case 'SUBMIT_FAILED':
      return 'The result could not be confirmed. Reload and check the ticket before retrying.';
    case 'RESTORED_UNCONFIRMED':
      // Deliberately the same instruction as SUBMIT_FAILED. From the
      // operator's side the situation is identical — the ticket may or may not
      // have come back — and telling them to retry would risk a second human
      // Restore act against a ticket that already had one.
      return 'The restore may have gone through, but the result could not be confirmed. Reload and check the ticket before retrying.';
    default:
      return '';
  }
}
